<template>
  <v-container fluid class="pl-4 pr-4 pb-4 pt-1">
    <!-- Compact header with bulk actions (height matches navigation divider) -->
    <v-row align="center" class="mb-0 px-0" style="min-height: 48px;">
      <v-col cols="auto" class="d-flex align-center ga-2">
        <v-select
          v-model="selectedBulkAction"
          :items="bulkActions"
          item-title="text"
          item-value="value"
          placeholder="Выберите действие"
          variant="outlined"
          density="compact"
          hide-details
          clearable
          :item-props="bulkItemProps"
          @update:model-value="executeBulkAction"
          style="min-width: 200px"
        ></v-select>
        <v-btn
          color="primary"
          variant="outlined"
          prepend-icon="mdi-plus"
          @click="openBulkAddModal"
        >
          Добавить каналы
        </v-btn>
      </v-col>
      <v-col class="text-center">
        <h4 class="mb-0">Телеграм каналы / чаты</h4>
      </v-col>
      <v-col cols="auto">
        <!-- Pagination controls with total count -->
        <div class="d-flex align-center ga-2">
          <span class="text-caption">На странице:</span>
          <v-select
            v-model="limit"
            :items="[20, 50, 100]"
            variant="outlined"
            density="compact"
            hide-details
            style="max-width: 160px"
            @update:model-value="onPageChange(1)"
          ></v-select>
          <v-pagination
            v-model="currentPage"
            :length="totalPages"
            :total-visible="5"
            size="small"
            @update:model-value="onPageChange"
          ></v-pagination>
          <span class="text-caption font-weight-bold"> Найдено: {{ totalItems }}</span>
          <v-btn
            :icon="panelVisible ? 'mdi-filter-off' : 'mdi-filter'"
            variant="outlined"
            @click="togglePanel"
            :color="panelVisible ? 'primary' : 'default'"
            class="ml-2"
          >
          </v-btn>
        </div>
      </v-col>
    </v-row>

    <v-row>
      <!-- Main table column -->
      <v-col :cols="panelVisible ? 10 : 12" class="table-column-transition">
        <!-- Table section -->
        <v-card>
          <v-card-text class="pa-0">
            <!-- server table: pages and sorting come from the API (a client v-data-table sorted
                 only the loaded page) -->
            <v-data-table-server
              :headers="headers"
              :items="telegramItems"
              :items-length="totalItems"
              :items-per-page="limit"
              :loading="isLoading"
              :sort-by="sortBy"
              @update:sort-by="updateSort"
              class="elevation-1"
              hover
              fixed-header
              height="calc(100vh - 236px)"
            >
          <!-- Checkbox column -->
          <template v-slot:[`item.checkbox`]="{ item }">
            <v-checkbox
              v-model="selectedItems"
              :value="item.id"
              hide-details
              density="compact"
            ></v-checkbox>
          </template>

          <!-- Logo column -->
          <template v-slot:[`item.logo`]="{ item }">
            <v-avatar size="40">
              <v-img
                v-if="avatarSrc(item)"
                :src="avatarSrc(item)"
                cover
              ></v-img>
              <v-icon v-else color="grey-lighten-1">mdi-cancel</v-icon>
            </v-avatar>
          </template>

          <!-- Channel name with link -->
          <template v-slot:[`item.telegram_username`]="{ item }">
            <a
              :href="`https://t.me/s/${formatUsername(item.telegram_username)}`"
              target="_blank"
              class="text-primary text-decoration-none"
            >
              {{ item.telegram_username }}
            </a>
          </template>

          <!-- Formatted numbers -->
          <!-- 0 of a channel never checked successfully means "unknown", not "no subscribers"
               (17.5k channels imported in August 2025 were never checked): a dash -->
          <template v-slot:[`item.followers`]="{ item }">
            <div class="text-right">
              <span v-if="!item.followers && !item.checked_at" class="text-medium-emphasis" title="Канал ещё не проверялся">—</span>
              <template v-else>{{ formatNumber(item.followers) }}</template>
            </div>
          </template>

          <!-- Messages count -->
          <template v-slot:[`item.total_messages_count`]="{ item }">
            <div 
              class="text-right"
              :class="{ 'text-primary cursor-pointer': item.total_messages_count > 0 }"
              @click="item.total_messages_count > 0 && openMessagesModal(item)"
            >
              {{ formatNumber(item.total_messages_count) }}
            </div>
          </template>

          <!-- Formatted date -->
          <!-- last check of the channel page that loaded: subscribers and avatar are from it -->
          <template v-slot:[`item.checked_at`]="{ item }">
            {{ item.checked_at ? formatISODate(item.checked_at) : '—' }}
          </template>

          <!-- the newest collected post; empty: nothing since the depth of collection -->
          <template v-slot:[`item.last_message_at`]="{ item }">
            {{ item.last_message_at ? formatISODate(item.last_message_at) : '—' }}
          </template>

          <!-- Created date -->
          <template v-slot:[`item.created_at`]="{ item }">
            {{ formatISODate(item.created_at) }}
          </template>

          <!-- Language -->
          <template v-slot:[`item.primary_lang`]="{ item }">
            {{ getLanguageName(item.primary_lang) }}
          </template>

          <!-- Title with tooltip -->
          <template v-slot:[`item.title`]="{ item }">
            <v-tooltip :text="item.title" location="top">
              <template v-slot:activator="{ props }">
                <span v-bind="props" class="text-truncate d-inline-block" style="max-width: 140px;">
                  {{ item.title }}
                </span>
              </template>
            </v-tooltip>
          </template>

          <!-- Resource type -->
          <template v-slot:[`item.resource_type`]="{ item }">
            {{ getEntityTypeName(item.resource_type) }}
          </template>

          <!-- Section (Topic) -->
          <template v-slot:[`item.network_name`]="{ item }">
            <v-chip v-if="item.is_aggregator" size="small" variant="tonal" color="deep-orange" title="Агрегатор: не участвует в кластеризации сетей">агрегатор</v-chip>
                <v-chip v-else-if="item.network_name" size="small" variant="tonal" color="deep-purple" :to="$store.getters.hasPermission('feature:osint') ? { path: '/monitoring/networks', query: { network: item.network_id } } : undefined" @click.stop>{{ item.network_name }}</v-chip>
            <span v-else class="text-caption text-grey">—</span>
          </template>
          <template v-slot:[`item.section`]="{ item }">
            {{ getTopicNames(item.topic_ids) }}
          </template>

          <!-- Country (Geolocation) -->
          <template v-slot:[`item.country`]="{ item }">
            {{ getGeolocationName(item.geolocation_id) }}
          </template>

          <!-- Status -->
          <template v-slot:[`item.status`]="{ item }">
            <v-chip
              :color="statusColor(item.status)"
              size="x-small"
              label
            >
              {{ statusLabel(item.status) }}
            </v-chip>
          </template>

          <!-- Access type -->
          <template v-slot:[`item.access_type`]="{ item }">
            {{ getAccessTypeName(item.access_type) }}
          </template>

          <!-- Is monitoring -->
          <template v-slot:[`item.is_monitoring`]="{ item }">
            {{ getIsMonitoring(item.is_monitoring) }}
          </template>

          <!-- Description with truncation -->
          <template v-slot:[`item.description`]="{ item }">
            <v-tooltip :text="item.description" location="top">
              <template v-slot:activator="{ props }">
                <span v-bind="props">{{ truncateDescription(item.description) }}</span>
              </template>
            </v-tooltip>
          </template>

          <!-- Actions column -->
          <template v-slot:[`item.actions`]="{ item }">
            <div class="d-flex justify-center">
              <v-tooltip text="Остановить" v-if="item.is_monitoring">
                <template v-slot:activator="{ props }">
                  <v-btn
                    v-bind="props"
                    icon="mdi-stop"
                    size="x-small"
                    variant="text"
                    color="warning"
                    @click="stopMonitoring(item, $event)"
                  ></v-btn>
                </template>
              </v-tooltip>
              <v-tooltip text="Запустить" v-else>
                <template v-slot:activator="{ props }">
                  <v-btn
                    v-bind="props"
                    icon="mdi-play"
                    size="x-small"
                    variant="text"
                    color="success"
                    @click="startMonitoring(item, $event)"
                    :disabled="item.is_processing"
                  ></v-btn>
                </template>
              </v-tooltip>
              <v-tooltip text="Обновить информацию">
                <template v-slot:activator="{ props }">
                  <v-btn
                    v-bind="props"
                    icon="mdi-information"
                    size="x-small"
                    variant="text"
                    @click="getInformation(item, $event)"
                    :loading="item.is_processing"
                    :disabled="item.is_monitoring"
                  ></v-btn>
                </template>
              </v-tooltip>
              <v-tooltip text="Граф связей">
                <template v-slot:activator="{ props }">
                  <v-btn
                    v-bind="props"
                    icon="mdi-graph-outline"
                    size="x-small"
                    variant="text"
                    color="primary"
                    @click.stop="openGraph(item)"
                  ></v-btn>
                </template>
              </v-tooltip>
              <v-tooltip text="Редактировать">
                <template v-slot:activator="{ props }">
                  <v-btn
                    v-bind="props"
                    icon="mdi-pencil"
                    size="x-small"
                    variant="text"
                    color="primary"
                    @click="openEditModal(item)"
                    :disabled="item.is_processing"
                  ></v-btn>
                </template>
              </v-tooltip>
              <v-tooltip text="Удалить">
                <template v-slot:activator="{ props }">
                  <v-btn
                    v-bind="props"
                    icon="mdi-delete"
                    size="x-small"
                    variant="text"
                    color="error"
                    @click="deleteChannel(item, $event)"
                    :disabled="item.is_monitoring || item.is_processing"
                  ></v-btn>
                </template>
              </v-tooltip>
            </div>
          </template>

          <!-- Table header checkbox -->
          <template v-slot:[`header.checkbox`]>
            <v-checkbox
              v-model="selectAll"
              @update:model-value="toggleSelectAll"
              hide-details
              density="compact"
            ></v-checkbox>
          </template>

          <!-- No data -->
          <template v-slot:no-data>
            <v-alert type="info" variant="text">
              Нет данных для отображения
            </v-alert>
          </template>

          <!-- Bottom slot - empty now since pagination moved to top -->
          <template v-slot:bottom>
            <div style="height: 1px;"></div>
          </template>
        </v-data-table-server>
      </v-card-text>
    </v-card>
      </v-col>

      <!-- Filters column with transition -->
      <v-slide-x-reverse-transition>
        <v-col v-if="panelVisible" cols="2" key="filters">
          <v-card>
            <v-card-title class="text-subtitle-1">Фильтры</v-card-title>
            <v-card-text class="pa-3">
              <!-- Followers range -->
              <div class="mb-4">
                <div class="text-subtitle-2 mb-2">Подписчики</div>
                <v-row dense>
                  <v-col cols="6">
                    <v-text-field
                      :model-value="telegramFilters.followersFrom"
                      @update:model-value="updateTelegramFilter({ field: 'followersFrom', value: $event })"
                      type="number"
                      step="1"
                      min="0"
                      placeholder="от"
                      variant="outlined"
                      density="compact"
                      hide-details
                      @blur="onFilterChange"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="6">
                    <v-text-field
                      :model-value="telegramFilters.followersTo"
                      @update:model-value="updateTelegramFilter({ field: 'followersTo', value: $event })"
                      type="number"
                      step="1"
                      min="0"
                      placeholder="до"
                      variant="outlined"
                      density="compact"
                      hide-details
                      @blur="onFilterChange"
                    ></v-text-field>
                  </v-col>
                </v-row>
              </div>

              <!-- Updated date range -->
              <div class="mb-4">
                <div class="text-subtitle-2 mb-2">Дата проверки</div>
                <v-text-field
                  :model-value="telegramFilters.updatedFrom"
                  @update:model-value="updateTelegramFilter({ field: 'updatedFrom', value: $event })"
                  type="date"
                  label="С"
                  variant="outlined"
                  density="compact"
                  hide-details
                  class="mb-2"
                  @change="onFilterChange"
                ></v-text-field>
                <v-text-field
                  :model-value="telegramFilters.updatedTo"
                  @update:model-value="updateTelegramFilter({ field: 'updatedTo', value: $event })"
                  type="date"
                  label="По"
                  variant="outlined"
                  density="compact"
                  hide-details
                  @change="onFilterChange"
                ></v-text-field>
              </div>

              <!-- Channels without posts since a day (or none collected since the depth of collection) -->
              <div class="mb-4">
                <div class="text-subtitle-2 mb-2">Последний пост раньше</div>
                <v-text-field
                  :model-value="telegramFilters.lastMessageBefore"
                  @update:model-value="updateTelegramFilter({ field: 'lastMessageBefore', value: $event })"
                  type="date"
                  label="Дата"
                  variant="outlined"
                  density="compact"
                  hide-details
                  clearable
                  @change="onFilterChange"
                  @click:clear="onFilterChange"
                ></v-text-field>
              </div>

              <!-- Created date range -->
              <div class="mb-4">
                <div class="text-subtitle-2 mb-2">Дата создания</div>
                <v-text-field
                  :model-value="telegramFilters.createdFrom"
                  @update:model-value="updateTelegramFilter({ field: 'createdFrom', value: $event })"
                  type="date"
                  label="С"
                  variant="outlined"
                  density="compact"
                  hide-details
                  class="mb-2"
                  @change="onFilterChange"
                ></v-text-field>
                <v-text-field
                  :model-value="telegramFilters.createdTo"
                  @update:model-value="updateTelegramFilter({ field: 'createdTo', value: $event })"
                  type="date"
                  label="По"
                  variant="outlined"
                  density="compact"
                  hide-details
                  @change="onFilterChange"
                ></v-text-field>
              </div>

              <!-- Channel title -->
              <v-text-field
                :model-value="telegramFilters.filterChannelName"
                @update:model-value="updateTelegramFilter({ field: 'filterChannelName', value: $event })"
                label="Заголовок канала"
                variant="outlined"
                density="compact"
                hide-details
                class="mb-4"
                @blur="onFilterChange"
              ></v-text-field>

              <!-- Username -->
              <v-text-field
                :model-value="telegramFilters.filterUsername"
                @update:model-value="updateTelegramFilter({ field: 'filterUsername', value: $event })"
                label="Имя канала"
                variant="outlined"
                density="compact"
                hide-details
                class="mb-4"
                @blur="onFilterChange"
              ></v-text-field>

              <!-- Channel ID -->
              <v-text-field
                :model-value="telegramFilters.filterChannelId"
                @update:model-value="updateTelegramFilter({ field: 'filterChannelId', value: $event })"
                label="ID канала"
                variant="outlined"
                density="compact"
                hide-details
                class="mb-4"
                @blur="onFilterChange"
              ></v-text-field>

              <!-- Description -->
              <v-text-field
                :model-value="telegramFilters.filterDescription"
                @update:model-value="updateTelegramFilter({ field: 'filterDescription', value: $event })"
                label="Описание"
                variant="outlined"
                density="compact"
                hide-details
                class="mb-4"
                @blur="onFilterChange"
              ></v-text-field>

              <!-- Country -->
              <v-select
                :model-value="telegramFilters.filterCountry"
                @update:model-value="updateTelegramFilter({ field: 'filterCountry', value: $event })"
                :items="countries"
                item-title="option"
                item-value="value"
                label="Страна"
                variant="outlined"
                density="compact"
                hide-details
                clearable
                class="mb-4"
              ></v-select>

              <!-- Language -->
              <v-select
                :model-value="telegramFilters.filterLanguage"
                @update:model-value="onLanguageFilterChange"
                :items="languageOptions"
                item-title="text"
                item-value="value"
                label="Язык"
                variant="outlined"
                density="compact"
                hide-details
                clearable
                class="mb-4"
              ></v-select>

              <!-- Telegram networks: one network, or only channels that are in any network -->
              <v-autocomplete
                :model-value="telegramFilters.filterNetwork"
                @update:model-value="updateTelegramFilter({ field: 'filterNetwork', value: $event })"
                :items="networkOptions"
                item-title="text"
                item-value="value"
                label="Сетка"
                variant="outlined"
                density="compact"
                hide-details
                clearable
                no-filter
                no-data-text="Сетки не найдены"
                class="mb-4"
                @update:search="searchNetworks"
              ></v-autocomplete>
              <v-switch
                :model-value="telegramFilters.filterInNetwork"
                @update:model-value="updateTelegramFilter({ field: 'filterInNetwork', value: $event })"
                label="Только каналы в сетках"
                color="primary"
                density="compact"
                hide-details
                class="mb-4"
              ></v-switch>

              <!-- Section -->
              <v-select
                :model-value="telegramFilters.filterSection"
                @update:model-value="updateTelegramFilter({ field: 'filterSection', value: $event })"
                :items="sectionFilterItems"
                item-title="option"
                item-value="value"
                label="Раздел"
                variant="outlined"
                density="compact"
                hide-details
                clearable
                class="mb-4"
              ></v-select>

              <!-- Type -->
              <v-select
                :model-value="telegramFilters.filterType"
                @update:model-value="updateTelegramFilter({ field: 'filterType', value: $event })"
                :items="typeOptions"
                item-title="text"
                item-value="value"
                label="Вид"
                variant="outlined"
                density="compact"
                hide-details
                clearable
                class="mb-4"
              ></v-select>

              <!-- Access type -->
              <v-select
                :model-value="telegramFilters.filterAccessType"
                @update:model-value="updateTelegramFilter({ field: 'filterAccessType', value: $event })"
                :items="accessTypeOptions"
                item-title="text"
                item-value="value"
                label="Тип доступа"
                variant="outlined"
                density="compact"
                hide-details
                clearable
                class="mb-4"
              ></v-select>

              <!-- Monitoring -->
              <v-select
                :model-value="telegramFilters.filterIsMonitoring"
                @update:model-value="updateTelegramFilter({ field: 'filterIsMonitoring', value: $event })"
                :items="monitoringOptions"
                item-title="text"
                item-value="value"
                label="Отслеживается"
                variant="outlined"
                density="compact"
                hide-details
                clearable
                class="mb-4"
              ></v-select>

              <!-- Tags -->
              <v-autocomplete
                :model-value="telegramFilters.filterTags"
                @update:model-value="onTagsUpdate"
                :items="availableTags"
                item-title="name"
                item-value="id"
                label="Теги"
                multiple
                chips
                closable-chips
                variant="outlined"
                density="compact"
                hide-details
                clearable
                class="mb-4"
                @update:search="searchAvailableTags"
                return-object
                ref="tagFilterAutocomplete"
              >
                <template #chip="{ props, item }">
                  <v-chip
                    v-bind="props"
                    :text="item.raw.name"
                    closable
                    size="small"
                    color="primary"
                  />
                </template>
              </v-autocomplete>

              <!-- Status -->
              <v-select
                :model-value="telegramFilters.filterStatus"
                @update:model-value="updateTelegramFilter({ field: 'filterStatus', value: $event })"
                :items="statusFilterOptions"
                item-title="text"
                item-value="value"
                label="Статус"
                variant="outlined"
                density="compact"
                hide-details
                clearable
                class="mb-4"
              ></v-select>

              <!-- Filter actions -->
              <div class="d-flex justify-space-between">
                <v-btn
                  color="primary"
                  variant="outlined"
                  @click="resetFilters"
                  prepend-icon="mdi-filter-remove"
                >
                  Сбросить
                </v-btn>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-slide-x-reverse-transition>
    </v-row>

    <!-- Edit dialog -->
    <v-dialog
      v-model="editModalVisible"
      max-width="600px"
      persistent
    >
      <v-card>
        <v-card-title>
          <span class="text-h5">Редактировать канал</span>
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12" class="text-center">
                <v-avatar size="70">
                  <v-img
                    v-if="avatarSrc(editItem)"
                    :src="avatarSrc(editItem)"
                    cover
                  ></v-img>
                  <v-icon v-else size="40" color="grey-lighten-1">mdi-cancel</v-icon>
                </v-avatar>
              </v-col>
            </v-row>
            
            <v-row dense>
              <v-col cols="12">
                <div class="mb-2"><strong>Тип:</strong> {{ getEntityTypeName(editItem.resource_type) }}</div>
                <div class="mb-2"><strong>Канал:</strong> {{ editItem.telegram_username }}</div>
                <div class="mb-2"><strong>Заголовок:</strong> {{ editItem.title }}</div>
                <div class="mb-2"><strong>Подписчики:</strong> {{ formatNumber(editItem.followers) }}</div>
                <div class="mb-2"><strong>Сообщений:</strong> {{ formatNumber(editItem.total_messages_count) }}</div>
                <div class="mb-2"><strong>Описание:</strong> {{ editItem.description }}</div>
              </v-col>
            </v-row>
            
            <v-divider class="my-4"></v-divider>
            
            <v-row dense>
              <v-col cols="12">
                <v-select
                  v-model="editForm.section"
                  :items="sections"
                  item-title="option"
                  item-value="value"
                  label="Раздел"
                  variant="outlined"
                  density="compact"
                ></v-select>
              </v-col>
              <v-col cols="12">
                <v-select
                  v-model="editForm.country"
                  :items="countries"
                  item-title="option"
                  item-value="value"
                  label="Страна"
                  variant="outlined"
                  density="compact"
                ></v-select>
              </v-col>
              <v-col cols="12">
                <v-select
                  v-model="editForm.status"
                  :items="statusOptions"
                  label="Статус"
                  variant="outlined"
                  density="compact"
                ></v-select>
              </v-col>
              <v-col cols="12">
                <v-autocomplete
                  v-model="editForm.tags"
                  :items="tagSuggestions"
                  item-title="name"
                  item-value="id"
                  label="Теги"
                  multiple
                  chips
                  closable-chips
                  variant="outlined"
                  density="compact"
                  @update:search="searchTags"
                  @keydown.enter="handleEnterKey"
                  @update:model-value="onTagSelected"
                  return-object
                  clearable
                  ref="tagAutocomplete"
                >
                  <template #chip="{ props, item }">
                    <v-chip
                      v-bind="props"
                      :text="getTagDisplayName(item)"
                      closable
                      size="small"
                      :color="item.raw.id ? 'primary' : 'success'"
                    />
                  </template>
                </v-autocomplete>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            variant="text"
            @click="closeEditModal"
          >
            Отменить
          </v-btn>
          <v-btn
            color="primary"
            variant="elevated"
            @click="saveEdit"
          >
            Сохранить
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Messages modal -->
    <MessagesModal
      :is-visible="messagesModalVisible"
      :channel-name="selectedChannelForMessages?.title || ''"
      :telegram-username="selectedChannelForMessages?.telegram_username || ''"
      :thumbnail-encoded="avatarSrc(selectedChannelForMessages)"
      @close="closeMessagesModal"
      @show-message="(message, isError) => $emit('show-message', message, isError)"
    />

    <!-- Bulk Edit Modal -->
    <v-dialog
      v-model="bulkEditModalVisible"
      max-width="500px"
      persistent
      @keydown.esc="closeBulkEditModal"
    >
      <v-card>
        <v-card-title>
          <span class="text-h5">Массовое редактирование</span>
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12">
                <div class="text-body-2 mb-2">
                  Выбрано каналов: <strong>{{ selectedItems.length }}</strong>
                </div>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-select
                  v-model="bulkEditForm.country"
                  :items="countries"
                  item-title="option"
                  item-value="value"
                  label="Страна"
                  variant="outlined"
                  density="compact"
                  clearable
                  hint="Будет применено ко всем выбранным каналам"
                  persistent-hint
                ></v-select>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-select
                  v-model="bulkEditForm.section"
                  :items="sections"
                  item-title="option"
                  item-value="value"
                  label="Раздел"
                  variant="outlined"
                  density="compact"
                  clearable
                  hint="Будет применено ко всем выбранным каналам"
                  persistent-hint
                ></v-select>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-select
                  v-model="bulkEditForm.status"
                  :items="statusOptions"
                  label="Статус"
                  variant="outlined"
                  density="compact"
                  clearable
                  hint="Будет применено ко всем выбранным каналам"
                  persistent-hint
                ></v-select>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-autocomplete
                  v-model="bulkEditForm.tags"
                  :items="bulkEditTagSuggestions"
                  item-title="name"
                  item-value="id"
                  label="Добавить теги"
                  multiple
                  chips
                  closable-chips
                  variant="outlined"
                  density="compact"
                  @update:search="searchBulkEditTags"
                  @keydown.enter="handleBulkEditEnterKey"
                  @update:model-value="onBulkEditTagSelected"
                  return-object
                  clearable
                  ref="bulkEditTagAutocomplete"
                  hint="Теги будут добавлены к существующим"
                  persistent-hint
                >
                  <template #chip="{ props, item }">
                    <v-chip
                      v-bind="props"
                      :text="getTagDisplayName(item)"
                      closable
                      size="small"
                      :color="item.raw.id ? 'primary' : 'success'"
                    />
                  </template>
                </v-autocomplete>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            variant="text"
            @click="closeBulkEditModal"
          >
            Отменить
          </v-btn>
          <v-btn
            color="primary"
            variant="elevated"
            @click="saveBulkEdit"
            :disabled="!hasBulkEditChanges()"
          >
            Сохранить
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog
      v-model="deleteConfirmVisible"
      max-width="450px"
      persistent
    >
      <v-card>
        <v-card-title class="text-h5">
          Удаление канала
        </v-card-title>
        <v-card-text>
          <div v-if="deleteConfirmItem">
            <p class="mb-3">
              Вы уверены, что хотите удалить канал
              <strong>{{ deleteConfirmItem.telegram_username }}</strong>?
            </p>

            <v-alert
              v-if="deleteContentStats && (deleteContentStats.messages_count > 0 || deleteContentStats.findings_count > 0)"
              type="warning"
              variant="tonal"
              class="mb-3"
            >
              <div v-if="deleteContentStats.messages_count > 0">
                Будет удалено сообщений: <strong>{{ deleteContentStats.messages_count }}</strong>
              </div>
              <div v-if="deleteContentStats.findings_count > 0">
                Будет удалено findings: <strong>{{ deleteContentStats.findings_count }}</strong>
              </div>
            </v-alert>

            <p class="text-body-2 text-grey">
              Это действие нельзя отменить.
            </p>
          </div>
          <div v-else-if="deleteLoading" class="text-center py-4">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            variant="text"
            @click="closeDeleteConfirm"
          >
            Отменить
          </v-btn>
          <v-btn
            color="error"
            variant="elevated"
            @click="confirmDelete"
            :disabled="deleteLoading"
          >
            Удалить
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Start / stop monitoring by filter: the numbers of the whole selection, then the run -->
    <v-dialog v-model="filterAction.visible" max-width="480px" persistent>
      <v-card>
        <v-card-title class="text-h5">
          {{ filterAction.action === 'start' ? 'Запустить мониторинг по фильтру' : 'Остановить мониторинг по фильтру' }}
        </v-card-title>
        <v-card-text>
          <p class="mb-3">Действие применяется ко всем каналам под текущими фильтрами, не только к этой странице.</p>
          <v-alert type="warning" variant="tonal" class="mb-3">
            Под фильтр попадает каналов: <strong>{{ filterAction.matched }}</strong><br>
            Мониторинг {{ filterAction.action === 'start' ? 'запустится' : 'остановится' }} у: <strong>{{ filterAction.changed }}</strong>
          </v-alert>
          <p class="text-body-2 text-grey">Меняется только отметка мониторинга, данные каналов не удаляются.</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" :disabled="filterAction.running" @click="filterAction.visible = false">Отменить</v-btn>
          <v-btn
            :color="filterAction.action === 'start' ? 'primary' : 'error'"
            variant="elevated"
            :loading="filterAction.running"
            :disabled="filterAction.changed === 0"
            @click="confirmFilterAction"
          >
            {{ filterAction.action === 'start' ? 'Запустить' : 'Остановить' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Bulk Delete Confirmation Dialog -->
    <v-dialog
      v-model="bulkDeleteConfirmVisible"
      max-width="450px"
      persistent
    >
      <v-card>
        <v-card-title class="text-h5">
          Удаление каналов
        </v-card-title>
        <v-card-text>
          <p class="mb-3">
            Вы уверены, что хотите удалить выбранные каналы?
          </p>
          <v-alert
            type="warning"
            variant="tonal"
            class="mb-3"
          >
            Будет удалено каналов: <strong>{{ selectedItems.length }}</strong>
          </v-alert>
          <p class="text-body-2 text-grey">
            Это действие нельзя отменить.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            variant="text"
            @click="cancelBulkDelete"
          >
            Отменить
          </v-btn>
          <v-btn
            color="error"
            variant="elevated"
            @click="confirmBulkDelete"
          >
            Удалить
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Bulk Add Channels Modal -->
    <v-dialog
      v-model="bulkAddModalVisible"
      max-width="700px"
      persistent
    >
      <v-card>
        <v-card-title>
          <span class="text-h5">Добавить каналы</span>
        </v-card-title>
        <v-card-text>
          <v-container>
            <!-- Step 1: Input channels -->
            <div v-if="bulkAddStep === 1">
              <v-row>
                <v-col cols="12">
                  <v-textarea
                    v-model="bulkAddForm.channelsText"
                    label="Список каналов"
                    placeholder="Введите каналы (по одному на строку)&#10;@channel1&#10;channel2&#10;@channel3"
                    rows="8"
                    variant="outlined"
                    hint="Можно указывать с @ или без. Каждый канал на новой строке."
                    persistent-hint
                  ></v-textarea>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="bulkAddForm.country"
                    :items="countries"
                    item-title="option"
                    item-value="value"
                    label="Страна"
                    variant="outlined"
                    density="compact"
                    clearable
                  ></v-select>
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="bulkAddForm.section"
                    :items="sections"
                    item-title="option"
                    item-value="value"
                    label="Раздел"
                    variant="outlined"
                    density="compact"
                    clearable
                  ></v-select>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <v-autocomplete
                    v-model="bulkAddForm.tags"
                    :items="bulkAddTagSuggestions"
                    item-title="name"
                    item-value="id"
                    label="Теги"
                    multiple
                    chips
                    closable-chips
                    variant="outlined"
                    density="compact"
                    @update:search="searchBulkAddTags"
                    @keydown.enter="handleBulkAddEnterKey"
                    @update:model-value="onBulkAddTagSelected"
                    return-object
                    clearable
                    ref="bulkAddTagAutocomplete"
                  >
                    <template #chip="{ props, item }">
                      <v-chip
                        v-bind="props"
                        :text="getTagDisplayName(item)"
                        closable
                        size="small"
                        :color="item.raw.id ? 'primary' : 'success'"
                      />
                    </template>
                  </v-autocomplete>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <v-checkbox
                    v-model="bulkAddForm.isMonitoring"
                    label="Включить мониторинг сразу после добавления"
                    hide-details
                    density="compact"
                  ></v-checkbox>
                </v-col>
              </v-row>
            </div>

            <!-- Step 2: Preview -->
            <div v-if="bulkAddStep === 2">
              <v-alert
                v-if="bulkAddPreview.existing.length > 0"
                type="warning"
                variant="tonal"
                class="mb-4"
              >
                <div class="font-weight-bold mb-2">Следующие каналы уже существуют ({{ bulkAddPreview.existing.length }}):</div>
                <div class="text-body-2">{{ bulkAddPreview.existing.join(', ') }}</div>
              </v-alert>

              <v-alert
                v-if="bulkAddPreview.new.length > 0"
                type="success"
                variant="tonal"
                class="mb-4"
              >
                <div class="font-weight-bold mb-2">Следующие каналы будут добавлены ({{ bulkAddPreview.new.length }}):</div>
                <div class="text-body-2">{{ bulkAddPreview.new.join(', ') }}</div>
              </v-alert>

              <v-alert
                v-if="bulkAddPreview.new.length === 0"
                type="info"
                variant="tonal"
                class="mb-4"
              >
                Все указанные каналы уже существуют в базе.
              </v-alert>

              <v-divider class="my-4"></v-divider>

              <div class="text-body-2">
                <div v-if="bulkAddForm.country">
                  <strong>Страна:</strong> {{ getCountryName(bulkAddForm.country) }}
                </div>
                <div v-if="bulkAddForm.section">
                  <strong>Раздел:</strong> {{ getSectionName(bulkAddForm.section) }}
                </div>
                <div v-if="bulkAddForm.tags && bulkAddForm.tags.length > 0">
                  <strong>Теги:</strong> {{ bulkAddForm.tags.map(t => t.name).join(', ') }}
                </div>
                <div>
                  <strong>Мониторинг:</strong> {{ bulkAddForm.isMonitoring ? 'Да' : 'Нет' }}
                </div>
              </div>
            </div>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            variant="text"
            @click="closeBulkAddModal"
          >
            Отменить
          </v-btn>
          <v-btn
            v-if="bulkAddStep === 1"
            color="primary"
            variant="elevated"
            @click="checkBulkAddChannels"
            :loading="bulkAddLoading"
            :disabled="!bulkAddForm.channelsText.trim()"
          >
            Проверить
          </v-btn>
          <v-btn
            v-if="bulkAddStep === 2"
            variant="text"
            @click="bulkAddStep = 1"
          >
            Назад
          </v-btn>
          <v-btn
            v-if="bulkAddStep === 2"
            color="primary"
            variant="elevated"
            @click="submitBulkAdd"
            :loading="bulkAddLoading"
            :disabled="bulkAddPreview.new.length === 0"
          >
            Добавить
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  <ChannelGraphModal
      v-model="showGraph"
      :source-id="graphTarget.sourceId"
      :username="graphTarget.username"
      :title="graphTarget.title"
    />
  </v-container>
</template>

<script>
import ChannelGraphModal from '@/components/ChannelGraphModal.vue';
import { mapGetters, mapActions, mapState } from 'vuex';
import { debounce } from 'lodash';
import apiClient from "@/apiClient";
import apiDataClient from "@/apiDataClient";
import MessagesModal from "@/components/MessagesModal.vue";
import { avatarSrc } from "@/utils/avatar";

// Sortable columns -> sort fields of GET /telegram (core platform_source_repository
// allowed_sort_fields); a column missing here is not sortable (the server would ignore it)
const TELEGRAM_SORT_FIELDS = {
  title: 'display_name',
  status: 'status',
  followers: 'subscribers',
  checked_at: 'checked_at',
  created_at: 'created_at',
  total_messages_count: 'messages_count',
  last_message_at: 'last_message_at',
};

export default {
  name: "TelegramSection",
  components: {
    MessagesModal, ChannelGraphModal },
  emits: ["show-message"],
  data() {
    return {
      showGraph: false,
      graphTarget: { sourceId: null, username: '' },
      // Table headers
      headers: [
        { title: '', key: 'checkbox', sortable: false, width: 90 },
        { title: '', key: 'logo', sortable: false, width: 80 },
        { title: 'Тип', key: 'resource_type', width: 80, sortable: false },
        { title: 'Канал', key: 'telegram_username', width: 130, sortable: false },
        { title: 'Заголовок', key: 'title', width: 150 },
        { title: 'Раздел', key: 'section', width: 100, sortable: false },
        { title: 'В сети', key: 'network_name', width: 130, sortable: false },
        { title: 'Страна', key: 'country', width: 70, sortable: false },
        { title: 'Статус', key: 'status', width: 100 },
        { title: 'Подписчики', key: 'followers', align: 'end', width: 110 },
        { title: 'Проверено', key: 'checked_at', width: 110 },
        { title: 'Создано', key: 'created_at', width: 110 },
        { title: 'Сообщений', key: 'total_messages_count', align: 'end', width: 100 },
        { title: 'Последний пост', key: 'last_message_at', width: 110 },
        { title: 'Доступ', key: 'access_type', width: 80, sortable: false },
        { title: 'Следить', key: 'is_monitoring', width: 70, sortable: false },
        { title: 'Язык', key: 'primary_lang', width: 80, sortable: false },
        { title: 'Описание', key: 'description', width: 180, sortable: false },
        { title: 'Действия', key: 'actions', sortable: false, width: 170 }
      ],
      // Local state
      telegramItems: [],
      selectedItems: [],
      selectAll: false,
      selectedBulkAction: '',
      sortBy: [],
      // Loading flag
      isLoading: false,
      editModalVisible: false,
      editItem: {},
      editForm: {
        section: null,
        country: null,
        status: null,
        tags: [],
      },
      statusOptions: [
        { title: 'Активен', value: 'active' },
        { title: 'В ожидании', value: 'pending' },
        { title: 'Заблокирован', value: 'blocked' },
        { title: 'Удалён', value: 'deleted' }
      ],
      tagInput: '',
      tagSuggestions: [],
      currentSearchValue: '',
      // Messages modal
      messagesModalVisible: false,
      selectedChannelForMessages: null,
      // Options
      bulkActions: [
        { text: 'Редактировать', value: 'edit' },
        { text: 'Обновить информацию', value: 'update' },
        { text: 'Определить раздел', value: 'classify' },
        { text: 'Запустить', value: 'start' },
        { text: 'Остановить', value: 'stop' },
        { text: 'Удалить', value: 'delete' },
        // the whole selection of the filters, not only the loaded page
        { text: 'Запустить по фильтру', value: 'start_filter', byFilter: true },
        { text: 'Остановить по фильтру', value: 'stop_filter', byFilter: true },
      ],
      // Bulk delete confirmation
      bulkDeleteConfirmVisible: false,
      // start / stop monitoring of the whole filtered selection: counts first, then the run
      filterAction: { visible: false, action: '', matched: 0, changed: 0, running: false },
      // Bulk edit modal
      bulkEditModalVisible: false,
      bulkEditForm: {
        tags: [],
        country: null,
        section: null,
        status: null
      },
      bulkEditInitialForm: null,
      bulkEditTagSuggestions: [],
      bulkEditCurrentSearchValue: '',
      // Delete confirmation
      deleteConfirmVisible: false,
      deleteConfirmItem: null,
      deleteContentStats: null,
      deleteLoading: false,
      // Bulk add modal
      bulkAddModalVisible: false,
      bulkAddStep: 1,
      bulkAddLoading: false,
      bulkAddForm: {
        channelsText: '',
        country: null,
        section: null,
        tags: [],
        isMonitoring: false
      },
      bulkAddTagSuggestions: [],
      bulkAddCurrentSearchValue: '',
      bulkAddPreview: {
        existing: [],
        new: []
      },
      // Reference data
      countries: [],
      sections: [],
      languageOptions: [],
      languageMap: {},
      // Filter options
      typeOptions: [
        { text: 'Все', value: '' },
        { text: 'Канал', value: 'channel' },
        { text: 'Группа', value: 'group' }
      ],
      accessTypeOptions: [
        { text: 'Все', value: '' },
        { text: 'Публичный web-канал', value: 'PUBLIC_WEB_CHANNEL' },
        { text: 'Публичный канал', value: 'PUBLIC_CHANNEL' },
        { text: 'Приватный канал', value: 'PRIVATE_CHANNEL' },
        { text: 'Публичная группа', value: 'PUBLIC_GROUP' },
        { text: 'Приватная группа', value: 'PRIVATE_GROUP' },
        { text: 'Бот', value: 'BOT' },
        { text: 'Неизвестный', value: 'UNKNOWN' },
        { text: 'Ошибка проверки', value: 'UNABLE_TO_CHECK' },
        { text: 'N/A (не проверен)', value: 'NOT_CHECKED' }
      ],
      monitoringOptions: [
        { text: 'Все', value: '' },
        { text: 'Да', value: '1' },
        { text: 'Нет', value: '0' }
      ],
      statusFilterOptions: [
        { text: 'Все', value: '' },
        { text: 'Активный', value: 'active' },
        { text: 'Ожидает', value: 'pending' },
        { text: 'Удалён', value: 'deleted' },
        { text: 'Заблокирован', value: 'blocked' }
      ],
      // For tags
      availableTags: [],
      networkOptions: [],
      networkSearchTimer: null,
      // Flag to prevent double API calls on initial load
      isInitialLoad: true,
      // Auto-refresh
      autoRefreshInterval: null,
      refreshRate: 30, // default, will be loaded from settings
      // Processing status polling
      processingPollInterval: null,
    };
  },
  computed: {
    // -1: channels without a topic (core NO_TOPIC) - to send to «Определить раздел» by 100
    sectionFilterItems() {
      return [{ option: 'Без раздела', value: -1 }, ...this.sections];
    },
    ...mapGetters('filters', ['telegramFilters']),
    ...mapState('filters', ['panelVisible']),
    
    // Map filters from store to local computed properties
    currentPage: {
      get() { return this.telegramFilters.currentPage; },
      set(value) { this.setTelegramPagination({ currentPage: value }); }
    },
    totalPages() {
      return this.telegramFilters.totalPages;
    },
    totalItems() {
      return this.telegramFilters.totalItems;
    },
    limit: {
      get() { return this.telegramFilters.limit; },
      set(value) { this.updateTelegramFilter({ field: 'limit', value }); }
    }
  },
  methods: {
    openGraph(item) {
      this.graphTarget = { sourceId: item.id, username: item.telegram_username, title: item.title || '' };
      this.showGraph = true;
    },
    ...mapActions('filters', [
      'setActiveFilterSet',
      'updateTelegramFilter',
      'setTelegramPagination',
      'setTelegramSorting',
      'loadTelegramFiltersFromCache',
      'saveTelegramFiltersToCache',
      'resetTelegramFilters',
      'togglePanel'
    ]),
    
    getLanguageName(code) {
      if (!code) return '';
      return (this.languageMap && this.languageMap[code]) || code;
    },

    onLanguageFilterChange(value) {
      this.updateTelegramFilter({ field: 'filterLanguage', value });
      this.onFilterChange();
    },

    updateSort(sortBy) {
      if (sortBy.length > 0) {
        const sortField = sortBy[0].key;
        const sortDirection = sortBy[0].order === 'desc' ? 'desc' : 'asc';
        this.setTelegramSorting({ sortField, sortDirection });
      } else {
        this.setTelegramSorting({ sortField: '', sortDirection: 'asc' });
      }
      this.currentPage = 1;
      this.fetchTelegramData();
    },
    
    truncateDescription(text) {
      if (!text) return "";
      return text.length > 100 ? text.substring(0, 100) + "..." : text;
    },
    
    avatarSrc,
    getImageSrc(encoded) {
      if (!encoded) return '';
      if (encoded.startsWith('data:image')) {
        return encoded;
      }
      return `data:image/jpeg;base64,${encoded}`;
    },
    
    formatNumber(value) {
      if (value == null) return '';
      return value.toLocaleString('ru-RU');
    },
    
    // Filters of the list as the API takes them (GET /telegram and POST /telegram/bulk/by-filter
    // select the same channels); paging and sorting are added by the caller
    filterParams() {
      const filters = this.telegramFilters;
      const params = {
        followers_from: filters.followersFrom,
        followers_to: filters.followersTo,
        // the day of the last successful check (column «Проверено»), not of the last write
        checked_from: filters.updatedFrom || undefined,
        checked_to: filters.updatedTo || undefined,
        last_message_before: filters.lastMessageBefore || undefined,
        created_from: filters.createdFrom || undefined,
        created_to: filters.createdTo || undefined,
        primary_lang: filters.filterLanguage || undefined,
        geolocation_id: filters.filterCountry || undefined,
        topic_id: filters.filterSection || undefined,
        channel_name: filters.filterChannelName || undefined,
        username: filters.filterUsername || undefined,
        channel_id: filters.filterChannelId || undefined,
        description: filters.filterDescription || undefined,
        entity_type: filters.filterType || undefined,
        access_type: filters.filterAccessType || undefined,
        network_id: filters.filterNetwork || undefined,
        in_network: filters.filterInNetwork || undefined,
        is_monitoring: filters.filterIsMonitoring || undefined,
        status: filters.filterStatus || undefined,
      };
      if (filters.filterTags && filters.filterTags.length > 0) {
        params.tags = filters.filterTags.map(tag => tag.id || tag).join(',');
      }
      return params;
    },

    async fetchTelegramData() {
      this.isLoading = true;
      try {
        const filters = this.telegramFilters;
        const offset = (filters.currentPage - 1) * filters.limit;
        const params = {
          ...this.filterParams(),
          offset,
          limit: filters.limit,
          sort_by: TELEGRAM_SORT_FIELDS[filters.sortField] || undefined,
          sort_order: filters.sortDirection || 'asc',
        };

        const resp = await apiClient.get("/telegram", { params });
        this.telegramItems = resp.data.items;

        // Update pagination in store
        this.setTelegramPagination({
          totalItems: resp.data.total,
          totalPages: Math.ceil(resp.data.total / filters.limit)
        });

        // Update sortBy array for v-data-table
        if (filters.sortField) {
          this.sortBy = [{ key: filters.sortField, order: filters.sortDirection }];
        }

        // Start polling if any items are being processed
        if (this.telegramItems.some(i => i.is_processing)) {
          this.startProcessingPoll();
        }
      } catch (err) {
        console.error("Ошибка при загрузке данных:", err);
      } finally {
        this.isLoading = false;
      }
    },
    
    onFilterChange() {
      // Skip if this is the initial load to prevent double API calls
      if (this.isInitialLoad) {
        return;
      }
      // Trim whitespace from text filter fields
      for (const field of ['filterChannelName', 'filterUsername', 'filterChannelId', 'filterDescription']) {
        const val = this.telegramFilters[field];
        if (val && val !== val.trim()) {
          this.updateTelegramFilter({ field, value: val.trim() });
        }
      }
      this.currentPage = 1;
      this.saveTelegramFiltersToCache();
      this.fetchTelegramData();
    },
    
    onPageChange(page) {
      // Skip if this is the initial load to prevent double API calls
      if (this.isInitialLoad) {
        return;
      }
      if (page < 1 || page > this.totalPages) return;
      this.currentPage = page;
      this.saveTelegramFiltersToCache();
      this.fetchTelegramData();
    },
    
    resetFilters() {
      this.resetTelegramFilters();
      this.fetchTelegramData();
    },
    
    // Current networks for the «Сетка» filter, largest first; typing searches by the name of the
    // network or a member username (GET /tg-networks, as on the networks page)
    searchNetworks(query) {
      clearTimeout(this.networkSearchTimer);
      this.networkSearchTimer = setTimeout(() => this.loadNetworks(query), 300);
    },
    async loadNetworks(query = '') {
      try {
        const resp = await apiClient.get('/tg-networks', { params: { search: query || undefined, page_size: 100 } });
        const options = resp.data.items.map(n => ({ value: n.id, text: `${n.hub_title || n.name} (${n.size})` }));
        // keep the chosen network in the list while searching for others
        const chosen = this.telegramFilters.filterNetwork;
        const current = this.networkOptions.find(o => o.value === chosen);
        if (current && !options.some(o => o.value === chosen)) options.unshift(current);
        this.networkOptions = options;
      } catch (e) {
        this.networkOptions = [];
      }
    },

    async searchAvailableTags(query) {
      if (!query) {
        this.availableTags = [];
        return;
      }
      try {
        const resp = await apiClient.get('/tags', { params: { q: query } });
        this.availableTags = resp.data.map(t => ({ id: t.id, name: t.name }));
      } catch (e) {
        this.availableTags = [];
      }
    },
    
    onTagsUpdate(tags) {
      // Обновляем теги в store
      this.updateTelegramFilter({ field: 'filterTags', value: tags });
      
      // Очищаем поле поиска и ставим фокус
      this.$nextTick(() => {
        if (this.$refs.tagFilterAutocomplete) {
          try {
            // Очищаем поисковое поле
            this.$refs.tagFilterAutocomplete.search = '';
            // Ставим фокус обратно в поле
            this.$refs.tagFilterAutocomplete.focus();
          } catch (e) {
            // Игнорируем ошибки доступа к ref
          }
        }
      });
    },
    
    async stopMonitoring(item) {
      if (item.is_monitoring) {
        console.log("Остановка мониторинга", item.telegram_username);
        await apiClient.post(`/telegram/channel/${item.telegram_username}/stop`)
          .then(response => {
            if (response.status === 200) {
              this.$emit('show-message','Мониторинг остановлен', false);
              item.is_monitoring = false;
              if (this.telegramFilters.filterIsMonitoring) {
                this.afterMonitoringChange([item.id], false);
                this.fetchTelegramData();
              }
            } else {
              this.$emit('show-message', 'Ошибка остановки мониторинга', true);
            }
          })
          .catch(error => {
            console.error("Error stopping monitoring:", error);
              this.$emit('show-message','Ошибка остановки мониторинга', true);
          });
      }
    },
    
    async startMonitoring(item) {
      if (!item.is_monitoring) {
        console.log("Запуск мониторинга", item.telegram_username);
        await apiClient.post(`/telegram/channel/${item.telegram_username}/start`)
          .then(response => {
            if (response.status === 200) {
              this.$emit('show-message','Мониторинг запущен', false);
              item.is_monitoring = true;
              if (this.telegramFilters.filterIsMonitoring) {
                this.afterMonitoringChange([item.id], true);
                this.fetchTelegramData();
              }
            } else {
              this.$emit('show-message','Ошибка запуска мониторинга', true);
            }
          })
          .catch(error => {
            console.error("Error starting monitoring:", error);
              this.$emit('show-message','Ошибка запуска мониторинга', true);
          });
      }
    },
    
    async getInformation(item) {
      try {
        await apiClient.post(`/telegram/channel/${item.telegram_username}/info`);
        item.is_processing = true;
        this.$emit('show-message', 'Получаем данные по телеграм каналу/чату', false);
        this.startProcessingPoll();
      } catch (error) {
        console.error("Error requesting info:", error);
        this.$emit('show-message', 'Ошибка получения данных по телеграм каналу/чату', true);
      }
    },
    
    formatUsername(username) {
      if (!username) return "";
      return username.startsWith("@") ? username.substring(1) : username;
    },
    
    async deleteChannel(item) {
      this.deleteConfirmItem = item;
      this.deleteLoading = true;
      this.deleteContentStats = null;

      // Get content stats from data service
      try {
        const response = await apiDataClient.get(`/sources/${item.id}/content-stats`);
        this.deleteContentStats = response.data;
      } catch (error) {
        console.error('Error getting content stats:', error);
        // Continue anyway - will show generic confirmation
      }

      this.deleteLoading = false;
      this.deleteConfirmVisible = true;
    },

    async confirmDelete() {
      if (!this.deleteConfirmItem) return;

      try {
        await apiClient.delete(`/telegram/${this.deleteConfirmItem.id}`);
        this.$emit('show-message', 'Канал успешно удален', false);
        this.closeDeleteConfirm();
        await this.fetchTelegramData();
      } catch (error) {
        console.error('Error deleting channel:', error);
        this.$emit('show-message', 'Ошибка удаления канала', true);
      }
    },

    closeDeleteConfirm() {
      this.deleteConfirmVisible = false;
      this.deleteConfirmItem = null;
      this.deleteContentStats = null;
    },

    // Bulk edit methods
    openBulkEditModal() {
      // Pre-compute common values from selected sources
      const selectedSources = this.telegramItems.filter(item => this.selectedItems.includes(item.id));

      // Country: if all share the same value, pre-load it
      const countries = selectedSources.map(s => s.geolocation_id);
      const commonCountry = countries.every(c => c === countries[0]) ? countries[0] : null;

      // Section: if all share the same value, pre-load it
      const sections = selectedSources.map(s => s.topic_ids && s.topic_ids.length > 0 ? s.topic_ids[0] : null);
      const commonSection = sections.every(s => s === sections[0]) ? sections[0] : null;

      // Status: if all share the same value, pre-load it
      const statuses = selectedSources.map(s => s.status);
      const commonStatus = statuses.every(s => s === statuses[0]) ? statuses[0] : null;

      // Tags: if all share the exact same tags, pre-load them
      let commonTags = [];
      if (selectedSources.length > 0) {
        const firstTags = (selectedSources[0].tags || []).map(t => t.id).sort().join(',');
        const allSameTags = selectedSources.every(s =>
          (s.tags || []).map(t => t.id).sort().join(',') === firstTags
        );
        if (allSameTags) {
          commonTags = (selectedSources[0].tags || []).map(t => ({ id: t.id, name: t.name }));
        }
      }

      this.bulkEditForm.country = commonCountry;
      this.bulkEditForm.section = commonSection;
      this.bulkEditForm.status = commonStatus;
      this.bulkEditForm.tags = commonTags;
      this.bulkEditInitialForm = {
        country: commonCountry,
        section: commonSection,
        status: commonStatus,
        tags: commonTags.map(t => ({ id: t.id, name: t.name })),
      };
      this.bulkEditTagSuggestions = [];
      this.bulkEditCurrentSearchValue = '';
      this.bulkEditModalVisible = true;
    },

    closeBulkEditModal() {
      this.bulkEditModalVisible = false;
      this.bulkEditForm.tags = [];
      this.bulkEditForm.country = null;
      this.bulkEditForm.section = null;
      this.bulkEditForm.status = null;
      this.bulkEditInitialForm = null;
      this.bulkEditTagSuggestions = [];
      this.bulkEditCurrentSearchValue = '';
      this.selectedBulkAction = '';
    },

    async searchBulkEditTags(query) {
      this.bulkEditCurrentSearchValue = query || '';
      if (!query) {
        this.bulkEditTagSuggestions = [];
        return;
      }
      try {
        const resp = await apiClient.get('/tags', { params: { q: query } });
        this.bulkEditTagSuggestions = resp.data.map(t => ({ id: t.id, name: t.name }));
      } catch (e) {
        this.bulkEditTagSuggestions = [];
      }
    },

    handleBulkEditEnterKey(event) {
      if (this.bulkEditCurrentSearchValue && this.bulkEditCurrentSearchValue.trim()) {
        event.preventDefault();
        this.addBulkEditNewTag(this.bulkEditCurrentSearchValue.trim());
      }
    },

    addBulkEditNewTag(tagName) {
      if (!tagName) return;

      const existingTag = this.bulkEditForm.tags.find(tag =>
        (tag.name && tag.name.toLowerCase() === tagName.toLowerCase()) ||
        (typeof tag === 'string' && tag.toLowerCase() === tagName.toLowerCase())
      );

      if (existingTag) return;

      const newTag = { name: tagName };
      this.bulkEditForm.tags.push(newTag);

      this.bulkEditCurrentSearchValue = '';
      this.$nextTick(() => {
        if (this.$refs.bulkEditTagAutocomplete) {
          try {
            this.$refs.bulkEditTagAutocomplete.search = '';
          } catch (e) {
            // Ignore ref access errors
          }
        }
      });
    },

    onBulkEditTagSelected() {
      this.bulkEditCurrentSearchValue = '';
      this.$nextTick(() => {
        if (this.$refs.bulkEditTagAutocomplete) {
          try {
            this.$refs.bulkEditTagAutocomplete.search = '';
            this.$refs.bulkEditTagAutocomplete.focus();
          } catch (e) {
            // Ignore ref access errors
          }
        }
      });
    },

    _prepareBulkEditTags(tags) {
      return (tags || []).map(tag => {
        if (typeof tag === 'object' && tag !== null && tag.id) {
          return { id: tag.id };
        }
        if (typeof tag === 'object' && tag !== null && tag.name && !tag.id) {
          return { name: tag.name };
        }
        if (typeof tag === 'string') {
          return { name: tag };
        }
        return null;
      }).filter(Boolean);
    },

    async saveBulkEdit() {
      try {
        const init = this.bulkEditInitialForm || {};
        const form = this.bulkEditForm;
        const payload = { channel_ids: this.selectedItems };

        // Only send fields that were changed (including cleared → null)
        if (form.country !== init.country) payload.geolocation_id = form.country;
        if (form.section !== init.section) payload.topic_id = form.section;
        if (form.status !== init.status) payload.status = form.status;

        const tagsChanged = JSON.stringify((form.tags || []).map(t => t.id || t.name)) !==
                            JSON.stringify((init.tags || []).map(t => t.id || t.name));
        if (tagsChanged) payload.tags = this._prepareBulkEditTags(form.tags);

        await apiClient.post('/telegram/bulk/edit', payload);

        this.$emit('show-message', `Обновлено ${this.selectedItems.length} каналов`, false);
        this.closeBulkEditModal();
        this.selectedItems = [];
        this.selectAll = false;
        this.isLoading = true;
        await this.fetchTelegramData();
      } catch (error) {
        console.error('Ошибка при массовом редактировании:', error);
        this.$emit('show-message', 'Ошибка при массовом редактировании', true);
      }
    },

    hasBulkEditChanges() {
      const init = this.bulkEditInitialForm || {};
      const form = this.bulkEditForm;
      return form.country !== init.country ||
             form.section !== init.section ||
             form.status !== init.status ||
             JSON.stringify((form.tags || []).map(t => t.id || t.name)) !==
             JSON.stringify((init.tags || []).map(t => t.id || t.name));
    },

    async openEditModal(item) {
      this.editItem = { ...item };
      // topic_ids - массив, берем первый элемент для v-select
      this.editForm.section = item.topic_ids && item.topic_ids.length > 0 ? item.topic_ids[0] : null;
      this.editForm.country = item.geolocation_id;
      this.editForm.status = item.status;
      this.tagInput = '';
      this.editForm.tags = (item.tags || []).map(t => ({ id: t.id, name: t.name }));
      this.editModalVisible = true;
      window.addEventListener('keydown', this.handleEscape);
    },

    async saveEdit() {
      try {
        const tagsToSend = this.editForm.tags.map(tag => {
          if (typeof tag === 'object' && tag !== null && tag.id) {
            return { id: tag.id };
          }
          if (typeof tag === 'object' && tag !== null && tag.name && !tag.id) {
            return { name: tag.name };
          }
          if (typeof tag === 'string') {
            return { name: tag };
          }
          return null;
        }).filter(Boolean);

        await apiClient.put(`/telegram/${this.editItem.id}`, {
          topic_id: this.editForm.section,
          geolocation_id: this.editForm.country,
          status: this.editForm.status,
          tags: tagsToSend,
        });
        this.$emit('show-message', 'Изменения успешно сохранены', false);
      } catch (error) {
        console.error("Ошибка при сохранении:", error);
        this.$emit('show-message', 'Не удалось сохранить изменения', true);
      } finally {
        this.closeEditModal();
        await this.fetchTelegramData();
      }
    },

    closeEditModal() {
      this.editModalVisible = false;
      this.editItem = {};
      this.editForm = { section: null, country: null, status: null, tags: [] };
      this.tagInput = '';
      this.currentSearchValue = '';
      window.removeEventListener('keydown', this.handleEscape);
    },
    
    async searchTags(query) {
      this.currentSearchValue = query || '';
      if (!query) {
        this.tagSuggestions = [];
        return;
      }
      try {
        const resp = await apiClient.get('/tags', { params: { q: query } });
        this.tagSuggestions = resp.data.map(t => ({ id: t.id, name: t.name }));
      } catch (e) {
        this.tagSuggestions = [];
      }
    },
    
    handleEnterKey(event) {
      if (this.currentSearchValue && this.currentSearchValue.trim()) {
        event.preventDefault();
        this.addNewTag(this.currentSearchValue.trim());
      }
    },
    
    addNewTag(tagName) {
      if (!tagName) return;
      
      const existingTag = this.editForm.tags.find(tag => 
        (tag.name && tag.name.toLowerCase() === tagName.toLowerCase()) ||
        (typeof tag === 'string' && tag.toLowerCase() === tagName.toLowerCase())
      );
      
      if (existingTag) return;
      
      const newTag = { name: tagName };
      this.editForm.tags.push(newTag);
      
      this.currentSearchValue = '';
      this.$nextTick(() => {
        if (this.$refs.tagAutocomplete) {
          try {
            this.$refs.tagAutocomplete.search = '';
          } catch (e) {
            // Ignore ref access errors
          }
        }
      });
    },
    
    getTagDisplayName(item) {
      if (item.raw) {
        return item.raw.name || item.raw;
      }
      return item.name || item;
    },
    
    onTagSelected() {
      this.currentSearchValue = '';
      this.$nextTick(() => {
        if (this.$refs.tagAutocomplete) {
          try {
            this.$refs.tagAutocomplete.search = '';
            this.$refs.tagAutocomplete.focus();
          } catch (e) {
            // Ignore ref access errors
          }
        }
      });
    },
    
    handleEscape(event) {
      if (event.key === "Escape") {
        this.closeEditModal();
      }
    },
    
    toggleSelectAll() {
      if (this.selectAll) {
        this.selectedItems = this.telegramItems.map(item => item.id);
      } else {
        this.selectedItems = [];
      }
    },
    
    bulkItemProps(item) {
      // actions on the chosen rows need a choice; those by filter work without one
      return { title: item.text, disabled: !item.byFilter && this.selectedItems.length === 0 };
    },

    async openFilterAction(action) {
      try {
        const resp = await apiClient.post('/telegram/bulk/by-filter', { action, dry_run: true, filters: this.filterParams() });
        this.filterAction = { visible: true, action, matched: resp.data.matched, changed: resp.data.changed, running: false };
      } catch (e) {
        const detail = e.response?.data?.detail;
        this.$emit('show-message', typeof detail === 'string' ? detail : 'Не удалось посчитать каналы по фильтру', true);
      } finally {
        this.selectedBulkAction = null;
      }
    },

    async confirmFilterAction() {
      this.filterAction.running = true;
      try {
        const resp = await apiClient.post('/telegram/bulk/by-filter', { action: this.filterAction.action, dry_run: false, filters: this.filterParams() });
        const verb = this.filterAction.action === 'start' ? 'запущен' : 'остановлен';
        this.$emit('show-message', `Мониторинг ${verb} у каналов: ${resp.data.changed}`, false);
        this.filterAction.visible = false;
        this.fetchTelegramData();
      } catch (e) {
        this.$emit('show-message', 'Не удалось изменить мониторинг по фильтру', true);
      } finally {
        this.filterAction.running = false;
      }
    },

    async executeBulkAction() {
      if (this.selectedBulkAction === 'start_filter' || this.selectedBulkAction === 'stop_filter') {
        await this.openFilterAction(this.selectedBulkAction === 'start_filter' ? 'start' : 'stop');
        return;
      }
      if (!this.selectedBulkAction || this.selectedItems.length === 0) return;

      try {
        if (this.selectedBulkAction === 'edit') {
          this.openBulkEditModal();
          return; // Don't clear selection yet
        } else if (this.selectedBulkAction === 'update') {
          await this.bulkUpdateInformation();
        } else if (this.selectedBulkAction === 'classify') {
          if (!(await this.bulkClassifyTopic())) return; // over 100: keep the selection
        } else if (this.selectedBulkAction === 'start') {
          await this.bulkStartMonitoring();
        } else if (this.selectedBulkAction === 'stop') {
          await this.bulkStopMonitoring();
        } else if (this.selectedBulkAction === 'delete') {
          this.bulkDeleteConfirmVisible = true;
          return; // Don't clear selection yet - wait for confirmation
        }

        this.selectedItems = [];
        this.selectAll = false;
      } catch (error) {
        console.error('Ошибка массовой операции:', error);
        this.$emit('show-message', 'Ошибка выполнения операции', true);
      } finally {
        this.selectedBulkAction = '';
      }
    },
    
    async bulkUpdateInformation() {
      await apiClient.post('/telegram/bulk/info', {
        channel_ids: this.selectedItems
      });

      this.telegramItems.forEach(item => {
        if (this.selectedItems.includes(item.id)) {
          item.is_processing = true;
        }
      });

      this.$emit('show-message', `Запущено обновление информации для ${this.selectedItems.length} каналов`, false);
      this.startProcessingPoll();
    },
    
    // topic and country by LLM: the channels go first in the next run (every 10 minutes); only an
    // empty topic or country is filled
    async bulkClassifyTopic() {
      if (this.selectedItems.length > 100) {
        this.$emit('show-message', `Определить раздел можно не больше чем для 100 каналов, выбрано ${this.selectedItems.length}`, true);
        return false;
      }
      const resp = await apiClient.post('/telegram/bulk/classify', {
        channel_ids: this.selectedItems
      });
      const { queued, already } = resp.data;
      const skipped = already ? `, ${already} уже с разделом и страной` : '';
      this.$emit('show-message', `Раздел будет определён для ${queued} каналов в течение 10 минут${skipped}`, false);
      return true;
    },

    // Channels that no longer match the list leave it at once and the counter drops; the reload
    // from the server (2-7 s on large lists) then recounts the page and the total
    dropFromList(ids) {
      const gone = new Set(ids);
      const before = this.telegramItems.length;
      this.telegramItems = this.telegramItems.filter(item => !gone.has(item.id));
      const total = Math.max(0, (this.totalItems || 0) - (before - this.telegramItems.length));
      this.setTelegramPagination({ totalItems: total, totalPages: Math.ceil(total / this.telegramFilters.limit) });
    },

    // after a start or stop: under «Отслеживается» the changed channels leave the list
    afterMonitoringChange(ids, monitoring) {
      const filter = this.telegramFilters.filterIsMonitoring;
      if (filter && (filter === '1') !== monitoring) this.dropFromList(ids);
      this.isLoading = true;
    },

    async bulkStartMonitoring() {
      await apiClient.post('/telegram/bulk/start', {
        channel_ids: this.selectedItems
      });

      this.telegramItems.forEach(item => {
        if (this.selectedItems.includes(item.id)) {
          item.is_monitoring = true;
        }
      });
      this.afterMonitoringChange(this.selectedItems, true);

      this.$emit('show-message', `Запущен мониторинг для ${this.selectedItems.length} каналов`, false);
      // the list again with its filters: under «Следить» the started/stopped channels move
      await this.fetchTelegramData();
    },

    async bulkStopMonitoring() {
      await apiClient.post('/telegram/bulk/stop', {
        channel_ids: this.selectedItems
      });

      this.telegramItems.forEach(item => {
        if (this.selectedItems.includes(item.id)) {
          item.is_monitoring = false;
        }
      });
      this.afterMonitoringChange(this.selectedItems, false);

      this.$emit('show-message', `Мониторинг остановлен для ${this.selectedItems.length} каналов`, false);
      // the list again with its filters: stopped channels leave a list filtered by «Следить»
      await this.fetchTelegramData();
    },

    async confirmBulkDelete() {
      try {
        const response = await apiClient.post('/telegram/bulk/delete', {
          channel_ids: this.selectedItems
        });

        // Remove deleted items from the list at once, then the page again from the server
        this.dropFromList(this.selectedItems);

        this.$emit('show-message', `Удалено ${response.data.deleted} каналов`, false);

        this.selectedItems = [];
        this.selectAll = false;
        await this.fetchTelegramData();
      } catch (error) {
        console.error('Ошибка массового удаления:', error);
        this.$emit('show-message', 'Ошибка при удалении каналов', true);
      } finally {
        this.bulkDeleteConfirmVisible = false;
        this.selectedBulkAction = '';
      }
    },

    cancelBulkDelete() {
      this.bulkDeleteConfirmVisible = false;
      this.selectedBulkAction = '';
    },

    openMessagesModal(item) {
      if (!item.total_messages_count || item.total_messages_count === 0) {
        this.$emit('show-message', 'У этого канала нет сохраненных сообщений', true);
        return;
      }
      this.selectedChannelForMessages = item;
      this.messagesModalVisible = true;
    },
    
    closeMessagesModal() {
      this.messagesModalVisible = false;
      this.selectedChannelForMessages = null;
    },
    
    getGeolocationName(geolocationId) {
      if (!geolocationId) return '';
      const country = this.countries.find(c => c.value === geolocationId);
      return country ? country.option : '';
    },

    getTopicNames(topicIds) {
      if (!topicIds || topicIds.length === 0) return '';
      const names = topicIds
        .map(id => {
          const topic = this.sections.find(s => s.value === id);
          return topic ? topic.option : null;
        })
        .filter(Boolean);
      return names.join(', ');
    },
    
    getEntityTypeName(code) {
      const types = {
        channel: 'Канал',
        chat: 'Чат'
      };
      return types[code] || '';
    },
    
    getAccessTypeName(code) {
      const types = {
        PUBLIC_WEB_CHANNEL: 'Публичный web-канал',
        PUBLIC_CHANNEL: 'Публичный канал',
        PRIVATE_CHANNEL: 'Приватный канал',
        PUBLIC_GROUP: 'Публичная группа',
        PRIVATE_GROUP: 'Приватная группа',
        BOT: 'Бот',
        UNKNOWN: 'Неизвестно',
        UNABLE_TO_CHECK: 'Ошибка проверки'
      };
      return types[code] || 'N/A';
    },
    
    getIsMonitoring(code) {
      return code === true ? 'Да' : 'Нет';
    },

    statusLabel(status) {
      const labels = {
        active: 'Активен',
        pending: 'В ожидании',
        blocked: 'Заблокирован',
        deleted: 'Удалён',
        error: 'Ошибка'
      };
      return labels[status] || status || 'В ожидании';
    },

    statusColor(status) {
      const colors = {
        active: 'success',
        pending: 'warning',
        blocked: 'error',
        deleted: 'grey',
        error: 'error'
      };
      return colors[status] || 'default';
    },
    
    formatISODate(isoString) {
      if (!isoString) {
        return 'не обновлялся';
      }
      const date = new Date(isoString);
      // Проверяем на epoch time (1970-01-01) или невалидную дату
      if (isNaN(date.getTime()) || date.getFullYear() === 1970) {
        return 'не обновлялся';
      }
      const dd = String(date.getDate()).padStart(2, '0');
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const yyyy = date.getFullYear();
      const HH = String(date.getHours()).padStart(2, '0');
      const MM = String(date.getMinutes()).padStart(2, '0');
      const ss = String(date.getSeconds()).padStart(2, '0');
      return `${dd}.${mm}.${yyyy} ${HH}:${MM}:${ss}`;
    },
    
    async loadReferenceData() {
      try {
        const [resCountries, resTopics, resLanguages] = await Promise.all([
          apiClient.get('/reference/geolocations/countries'),
          apiClient.get('/reference/topics'),
          apiClient.get('/languages', { params: { platform_id: 7 } })
        ]);
        // Transform to { option: name, value: id } format for v-select
        this.countries = resCountries.data.map(item => ({
          option: item.name,
          value: item.id
        }));
        this.sections = resTopics.data.items.map(item => ({
          option: item.name,
          value: item.id
        }));
        // Build language name map from reference data
        this.languageMap = {};
        resLanguages.data.forEach(item => {
          this.languageMap[item.code] = item.name;
        });
        // Filter options: only show languages that have sources
        this.languageOptions = resLanguages.data
          .filter(item => item.count > 0)
          .map(item => ({
            text: `${item.name} (${item.count})`,
            value: item.code
          }));
      } catch (error) {
        console.error('Error loading reference data:', error);
      }
    },

    // Bulk Add methods
    openBulkAddModal() {
      this.bulkAddStep = 1;
      this.bulkAddForm = {
        channelsText: '',
        country: null,
        section: null,
        tags: [],
        isMonitoring: false
      };
      this.bulkAddPreview = { existing: [], new: [] };
      this.bulkAddTagSuggestions = [];
      this.bulkAddCurrentSearchValue = '';
      this.bulkAddModalVisible = true;
    },

    closeBulkAddModal() {
      this.bulkAddModalVisible = false;
      this.bulkAddStep = 1;
      this.bulkAddForm = {
        channelsText: '',
        country: null,
        section: null,
        tags: [],
        isMonitoring: false
      };
      this.bulkAddPreview = { existing: [], new: [] };
    },

    async searchBulkAddTags(query) {
      this.bulkAddCurrentSearchValue = query || '';
      if (!query) {
        this.bulkAddTagSuggestions = [];
        return;
      }
      try {
        const resp = await apiClient.get('/tags', { params: { q: query } });
        this.bulkAddTagSuggestions = resp.data.map(t => ({ id: t.id, name: t.name }));
      } catch (e) {
        this.bulkAddTagSuggestions = [];
      }
    },

    handleBulkAddEnterKey(event) {
      if (this.bulkAddCurrentSearchValue && this.bulkAddCurrentSearchValue.trim()) {
        event.preventDefault();
        this.addBulkAddNewTag(this.bulkAddCurrentSearchValue.trim());
      }
    },

    addBulkAddNewTag(tagName) {
      if (!tagName) return;

      const existingTag = this.bulkAddForm.tags.find(tag =>
        (tag.name && tag.name.toLowerCase() === tagName.toLowerCase()) ||
        (typeof tag === 'string' && tag.toLowerCase() === tagName.toLowerCase())
      );

      if (existingTag) return;

      const newTag = { name: tagName };
      this.bulkAddForm.tags.push(newTag);

      this.bulkAddCurrentSearchValue = '';
      this.$nextTick(() => {
        if (this.$refs.bulkAddTagAutocomplete) {
          try {
            this.$refs.bulkAddTagAutocomplete.search = '';
          } catch (e) {
            // Ignore ref access errors
          }
        }
      });
    },

    onBulkAddTagSelected() {
      this.bulkAddCurrentSearchValue = '';
      this.$nextTick(() => {
        if (this.$refs.bulkAddTagAutocomplete) {
          try {
            this.$refs.bulkAddTagAutocomplete.search = '';
            this.$refs.bulkAddTagAutocomplete.focus();
          } catch (e) {
            // Ignore ref access errors
          }
        }
      });
    },

    parseChannelsText(text) {
      // Разбираем текст на строки и извлекаем каналы
      return text
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0)
        .map(line => line.replace(/^@/, '')); // Убираем @ в начале
    },

    async checkBulkAddChannels() {
      const usernames = this.parseChannelsText(this.bulkAddForm.channelsText);
      if (usernames.length === 0) {
        this.$emit('show-message', 'Введите хотя бы один канал', true);
        return;
      }

      this.bulkAddLoading = true;
      try {
        const resp = await apiClient.post('/telegram/bulk/check', { usernames });
        this.bulkAddPreview = {
          existing: resp.data.existing,
          new: resp.data.new
        };
        this.bulkAddStep = 2;
      } catch (error) {
        console.error('Error checking channels:', error);
        this.$emit('show-message', 'Ошибка при проверке каналов', true);
      } finally {
        this.bulkAddLoading = false;
      }
    },

    async submitBulkAdd() {
      if (this.bulkAddPreview.new.length === 0) {
        this.$emit('show-message', 'Нет каналов для добавления', true);
        return;
      }

      this.bulkAddLoading = true;
      try {
        const tagsToSend = this.bulkAddForm.tags.map(tag => {
          if (typeof tag === 'object' && tag !== null && tag.id) {
            return { id: tag.id };
          }
          if (typeof tag === 'object' && tag !== null && tag.name && !tag.id) {
            return { name: tag.name };
          }
          if (typeof tag === 'string') {
            return { name: tag };
          }
          return null;
        }).filter(Boolean);

        const payload = {
          usernames: this.bulkAddPreview.new,
          geolocation_id: this.bulkAddForm.country || null,
          topic_id: this.bulkAddForm.section || null,
          tags: tagsToSend.length > 0 ? tagsToSend : null,
          is_monitoring: this.bulkAddForm.isMonitoring
        };

        const resp = await apiClient.post('/telegram/bulk/add', payload);

        if (resp.data.created_count > 0) {
          this.$emit('show-message', `Добавлено ${resp.data.created_count} каналов`, false);
          this.closeBulkAddModal();
          await this.fetchTelegramData();
        } else {
          this.$emit('show-message', 'Не удалось добавить каналы', true);
        }

        if (resp.data.errors && resp.data.errors.length > 0) {
          console.warn('Bulk add errors:', resp.data.errors);
        }
      } catch (error) {
        console.error('Error adding channels:', error);
        this.$emit('show-message', 'Ошибка при добавлении каналов', true);
      } finally {
        this.bulkAddLoading = false;
      }
    },

    getCountryName(countryId) {
      const country = this.countries.find(c => c.value === countryId);
      return country ? country.option : '';
    },

    getSectionName(sectionId) {
      const section = this.sections.find(s => s.value === sectionId);
      return section ? section.option : '';
    },

    async loadRefreshSettings() {
      try {
        const response = await apiClient.get('/admin/settings', {
          params: { name: 'base' }
        });
        if (response.data && response.data.frontend_refresh_rate) {
          this.refreshRate = response.data.frontend_refresh_rate;
        }
      } catch (error) {
        console.error('Error loading refresh settings:', error);
        // Use default 30 seconds
      }
    },

    startAutoRefresh() {
      // Clear existing interval if any
      this.stopAutoRefresh();

      if (this.refreshRate > 0) {
        this.autoRefreshInterval = setInterval(() => {
          // Only refresh if not loading and no modal is open
          if (!this.isLoading && !this.editModalVisible && !this.bulkAddModalVisible && !this.bulkEditModalVisible && !this.deleteConfirmVisible && !this.bulkDeleteConfirmVisible && !this.filterAction.visible) {
            this.fetchTelegramData();
          }
        }, this.refreshRate * 1000);
      }
    },

    stopAutoRefresh() {
      if (this.autoRefreshInterval) {
        clearInterval(this.autoRefreshInterval);
        this.autoRefreshInterval = null;
      }
    },

    // Processing status polling — refetch table when sources finish processing
    startProcessingPoll() {
      if (this.processingPollInterval) return;
      this.processingPollInterval = setInterval(() => {
        this.checkProcessingStatus();
      }, 5000);
    },

    stopProcessingPoll() {
      if (this.processingPollInterval) {
        clearInterval(this.processingPollInterval);
        this.processingPollInterval = null;
      }
    },

    async checkProcessingStatus() {
      const processingItems = this.telegramItems.filter(i => i.is_processing);
      if (processingItems.length === 0) {
        this.stopProcessingPoll();
        return;
      }

      try {
        // Refetch current page data
        const filters = this.telegramFilters;
        const offset = (filters.currentPage - 1) * filters.limit;
        const params = {
          ...this.filterParams(),
          offset,
          limit: filters.limit,
          sort_by: TELEGRAM_SORT_FIELDS[filters.sortField] || undefined,
          sort_order: filters.sortDirection || 'asc',
        };


        const resp = await apiClient.get("/telegram", { params });
        const freshItems = resp.data.items;

        // Check which items finished processing
        const finishedNames = [];
        for (const oldItem of processingItems) {
          const freshItem = freshItems.find(i => i.id === oldItem.id);
          if (freshItem && !freshItem.is_processing) {
            finishedNames.push(freshItem.display_name || freshItem.telegram_username);
          }
        }

        // Update table data
        this.telegramItems = freshItems;
        this.setTelegramPagination({
          totalItems: resp.data.total,
          totalPages: Math.ceil(resp.data.total / filters.limit)
        });

        if (finishedNames.length > 0) {
          this.$emit('show-message', `Информация обновлена: ${finishedNames.join(', ')}`, false);
        }

        // Stop polling if no more processing
        if (!freshItems.some(i => i.is_processing)) {
          this.stopProcessingPoll();
        }
      } catch (error) {
        console.error('Error checking processing status:', error);
      }
    },
  },
  created() {
    // Create debounced version of onFilterChange to prevent multiple API calls
    this.debouncedFilterChange = debounce(() => {
      this.onFilterChange();
    }, 500);
  },
  async mounted() {
    // Activate telegram filters
    this.setActiveFilterSet('telegram');
    // Load cached filters
    this.loadTelegramFiltersFromCache();
    // Deep-link из графа связей: /admin/telegram?username=<канал> → фильтр по username
    if (this.$route && this.$route.query && this.$route.query.username) {
      this.updateTelegramFilter({ field: 'filterUsername', value: String(this.$route.query.username) });
    }
    // Load reference data
    await this.loadReferenceData();
    this.loadNetworks();
    // Load refresh settings and start auto-refresh
    await this.loadRefreshSettings();
    // Fetch data for the first time
    this.fetchTelegramData();
    // Auto-refresh disabled - uncomment to enable
    // this.startAutoRefresh();
    // Allow watchers to trigger API calls after initial load
    // Use setTimeout with delay longer than debounce to prevent double calls
    setTimeout(() => {
      this.isInitialLoad = false;
    }, 600);
  },
  beforeUnmount() {
    // Stop auto-refresh and processing poll
    this.stopAutoRefresh();
    this.stopProcessingPoll();
    // Deactivate filters when leaving
    this.setActiveFilterSet(null);
  },
  watch: {
    // Watch for filter changes from store with debounce to prevent multiple API calls
    'telegramFilters.followersFrom'() {
      this.debouncedFilterChange();
    },
    'telegramFilters.followersTo'() {
      this.debouncedFilterChange();
    },
    'telegramFilters.updatedFrom'() {
      this.debouncedFilterChange();
    },
    'telegramFilters.updatedTo'() {
      this.debouncedFilterChange();
    },
    'telegramFilters.filterCountry'() {
      this.debouncedFilterChange();
    },
    'telegramFilters.filterSection'() {
      this.debouncedFilterChange();
    },
    'telegramFilters.filterChannelName'() {
      this.debouncedFilterChange();
    },
    'telegramFilters.filterUsername'() {
      this.debouncedFilterChange();
    },
    'telegramFilters.filterChannelId'() {
      this.debouncedFilterChange();
    },
    'telegramFilters.filterDescription'() {
      this.debouncedFilterChange();
    },
    'telegramFilters.filterType'() {
      this.debouncedFilterChange();
    },
    'telegramFilters.filterAccessType'() {
      this.debouncedFilterChange();
    },
    'telegramFilters.filterIsMonitoring'() {
      this.debouncedFilterChange();
    },
    'telegramFilters.filterTags'() {
      this.debouncedFilterChange();
    },
    'telegramFilters.filterStatus'() {
      this.debouncedFilterChange();
    },
    'telegramFilters.filterNetwork'() {
      this.debouncedFilterChange();
    },
    'telegramFilters.filterInNetwork'() {
      this.debouncedFilterChange();
    }
  }
};
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}

.v-data-table {
  font-size: 0.875rem;
}

/* Ensure proper text truncation in table cells */
.v-data-table :deep(.v-data-table__td) {
  padding: 8px 16px !important;
  height: 60px !important;
}

.v-data-table :deep(.v-data-table__th) {
  padding: 8px 16px !important;
  font-weight: 600;
  white-space: nowrap;
}

/* Reduce spacing between columns except first two */
.v-data-table :deep(.v-data-table__td:not(:nth-child(1)):not(:nth-child(2))) {
  padding-left: 8px !important;
  padding-right: 8px !important;
}

.v-data-table :deep(.v-data-table__th:not(:nth-child(1)):not(:nth-child(2))) {
  padding-left: 8px !important;
  padding-right: 8px !important;
}

/* Checkbox column — compact with fixed width */
.v-data-table :deep(.v-data-table__td:nth-child(1)),
.v-data-table :deep(.v-data-table__th:nth-child(1)) {
  padding: 8px 4px 8px 8px !important;
  overflow: visible !important;
  width: 48px !important;
  min-width: 48px !important;
  max-width: 48px !important;
}

/* Logo column — fixed spacing from checkbox */
.v-data-table :deep(.v-data-table__td:nth-child(2)),
.v-data-table :deep(.v-data-table__th:nth-child(2)) {
  padding-left: 16px !important;
  min-width: 72px !important;
}

/* Ensure text doesn't wrap in cells */
.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Compact action buttons */
.v-btn--size-x-small {
  height: 28px !important;
  width: 28px !important;
}

/* Better alignment for numbers */
.text-right {
  text-align: right !important;
}

/* Table column transition */
.table-column-transition {
  transition: flex-basis 0.3s ease, max-width 0.3s ease;
}
</style>